/**
 * This is meant to be a replacement for initializeApp, as this one uses
 * crehana-i18n.
 */
import React from 'react';

import getRenderMethod from 'Jsx/Utils/getRenderMethod';
import loadServiceWorker from 'Jsx/ServiceWorker/loadServiceWorker';

import getBaseInitialState, {
  GetInitialStateInner,
  InitialStateResult,
  Options,
} from 'Jsx/Utils/getBaseInitialState';

import Layout, { LayoutProps } from './LayoutV2';

export type Params<
  TInitialState = any,
  TRawPageData = {},
  TExtraState = any
> = {
  // Main App component
  App: React.FC<InitialStateResult<TInitialState & TExtraState>>;
  // needed to apply the fallback for the specific translation group
  i18nConfig?: Options['i18nConfig'];
  getInitialState?: GetInitialStateInner<TInitialState, TRawPageData>;
  // The window.__INITIAL__.initialStatePageKey. Ex: catalog, courseDetail, lessons, checkout, etc.
  initialStatePageKey?: string;
  layoutProps?: Omit<LayoutProps, 'children'>;
  extraState?: TExtraState;
  // needed for ssr with react-jss
  // will render a jsx without i18n and apollo providers
  // lightMode?: boolean;
  // A callback to trigger actions after the main React Application is rendered
  onReactDOMRender?: () => void;
};

const render = getRenderMethod('#mainNode');

function initializeApp<TInitialState = {}, TRawPageData = {}>({
  App,
  getInitialState = () => ({} as TInitialState),
  initialStatePageKey,
  layoutProps,
  i18nConfig,
  extraState: extraStateProp,
  onReactDOMRender,
}: Params<TInitialState, TRawPageData>): void {
  const rawInitialState = window.__INITIAL_STATE_UNPARSED__
    ? JSON.parse(window.__INITIAL_STATE_UNPARSED__)
    : window.__INITIAL_STATE__;
  const initialState = getBaseInitialState<TInitialState, TRawPageData>(
    getInitialState,
    {
      i18nConfig,
      useCrehanaI18n: true,
    },
  )(
    initialStatePageKey ? rawInitialState[initialStatePageKey] : undefined,
    rawInitialState.user,
    rawInitialState,
  );

  const extraState = {
    innerWidth: window.innerWidth,
    ...extraStateProp,
  };

  const mainJsx = (
    <Layout
      crehanaI18n={initialState.crehanaI18n}
      colorTheme={initialState.colorTheme}
      {...layoutProps}
    >
      <div>
        <App {...initialState} {...extraState} />
      </div>
    </Layout>
  );

  return render(
    mainJsx,
    document.querySelector('#mainNode') as HTMLDivElement,
    () => {
      window.addEventListener('DOMContentLoaded', () => {
        if (onReactDOMRender) {
          onReactDOMRender();
        }
      });
      // service worker setup
      window.addEventListener('load', () => {
        if (window.requestIdleCallback) {
          window.requestIdleCallback(loadServiceWorker);
        }
      });
    },
  );
}

export default initializeApp;
